import { Box, Tooltip, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import DataTable, { DataTableFilter, DataTableFilterItem } from "../components/DataTable";
import Subheader from "../components/Subheader";
import Api from "../openapi";
import { PagedResponsePartnerUserListResponse } from "../openapi/client";
import {
  GridPaginationModel,
  GridCallbackDetails,
  GridSortModel,
  GridRenderCellParams,
  GridRowParams,
} from "@mui/x-data-grid";
import { ChannelIcon, checkIf, displayFormat, formatCurrency, generateRandom } from "../utils";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import UserCrud from "../components/UserCrud";
import useConfirm from "../hooks/useConfirm";
import { DeleteLabelIcon } from "../components/IconLabel";
import { useDispatch } from "react-redux";
import { setNotification } from "../store/notificationSlice";
import useQuery from "../hooks/useQuery";
import useChannels from "../hooks/useChannels";
import useDietitians from "../hooks/useDietitians";
import { usePlans } from "../hooks/usePlans";
import useMealTypes from "../hooks/useMealTypes";

export const getUserNameWithIcon = (name: string, channel: string) => {
  const icon = ChannelIcon(channel);
  return (
    <Box sx={{ display: "flex", gap: "8px", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
      <Typography sx={{ fontSize: "14px", fontWeight: 500 }} component={"span"}>
        {name}
      </Typography>
      {icon}
    </Box>
  );
};

export default function Users() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const dietitians = useDietitians();
  const plans = usePlans();
  const channels = useChannels();
  const query = useQuery();
  const mealTypeList = useMealTypes();

  const [users, setUsers] = useState<PagedResponsePartnerUserListResponse[]>([]);
  const [createShow, setCreateShow] = useState(false);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [retriggerReq, setRetriggerReq] = useState<number>();
  const [filterModel, setFilterModel] = useState<DataTableFilter>({
    minBalance: query.get("minBalance") || null,
    maxBalance: query.get("maxBalance") || null,
  });

  const [ConfirmDialog, confirmDelete] = useConfirm({
    titleIcon: <DeleteLabelIcon />,
    text: "You are about to permanently delete a user from the list, and this action is irreversible. Are you sure you want to proceed?",
  });

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: "fullName",
      sort: "asc",
    },
  ]);

  const handlePaginationChange = (model: GridPaginationModel, details: GridCallbackDetails<any>) => {
    setPaginationModel(model);
  };
  const handleSortChange = (sortModel: GridSortModel) => {
    sortModel.length && setSortModel(sortModel);
  };

  const create = () => {
    setCreateShow(true);
  };

  const deleteRec = async (params: any) => {
    const answer = await confirmDelete();
    if (!answer) return;

    try {
      const req = await Api.AdminMeal.deleteMeal(params.row.id);
      dispatch(
        setNotification({
          show: true,
          severity: "success",
          message: "Meal has been deleted successfully",
        })
      );
      setRetriggerReq(generateRandom());
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
  };

  const handleClose = () => {
    setCreateShow(false);
  };
  const handleConfirm = () => {
    setRetriggerReq(generateRandom());
  };

  const handleRowClick = (params: GridRowParams, event: any) => {
    // Check if clicking on a link wrapper (right-click, ctrl+click, middle-click should open in new tab)
    const target = event.target as HTMLElement;
    const linkWrapper = target.closest('.row-link-wrapper');

    // If clicking on link wrapper with left-click only (no modifiers)
    if (linkWrapper && event.button === 0 && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
      navigate(`/users/${params.row.id}`);
    }
    // Otherwise let the browser handle it naturally (Ctrl+Click, middle-click, right-click)
  };

  const getRowNavigationUrl = (rowId: any) => {
    return `/users/${rowId}`;
  };

  const getUserSubscriptionStatus = (params: GridRenderCellParams<any, any>) => {
    const userActive = Boolean(params.row.active);
    const subscriptionActive = Boolean(params.row.subscription?.active);
    const subscriptionStatus = userActive && subscriptionActive ? "Active" : "Inactive";
    const subscriptionClass = userActive && subscriptionActive ? "active" : "passive";
    return <Box className={`status-label ${subscriptionClass}`}>{subscriptionStatus}</Box>;
  };

  const columns = useMemo(() => {
    return [
      {
        field: "externalId",
        minWidth: 80,
        flex: 0,
        headerName: "Member ID",
        valueGetter: (params: any) => params.row.externalId,
      },
      {
        field: "fullName",
        minWidth: 150,
        flex: 2,
        headerName: "Name",
        renderCell: (params: any) => getUserNameWithIcon(params.row.fullName, params.row.channel),
      },
      {
        field: "phoneNumber",
        minWidth: 120,
        flex: 1,
        headerName: "Phone Number",
        valueGetter: (params: any) => params.row.phoneNumber,
      },
      {
        field: "birthDate",
        minWidth: 80,
        flex: 0,
        headerName: "Birth Date",
        valueGetter: (params: any) =>
          checkIf(params.row.birthDate) ? moment(params.row.birthDate).format(displayFormat) : "",
      },
      {
        field: "gender",
        minWidth: 80,
        flex: 0,
        headerName: "Gender",
        valueGetter: (params: any) => params.row.gender,
      },
      {
        field: "totalBalance",
        minWidth: 80,
        flex: 1,
        headerName: "Balance",
        valueGetter: (params: any) => formatCurrency(params.row.totalBalance),
      },
      {
        field: "dietitianFullName",
        minWidth: 80,
        flex: 1,
        headerName: "Dietitian",
        valueGetter: (params: any) => params.row.dietitianFullName || "-",
      },
      {
        field: "subscription.planName,subscription.planOptionSelection",
        minWidth: 200,
        sortable: true,
        flex: 2,
        headerName: "Subscription",
        renderCell: (params: any) => (
          params.row.subscription && (
          <Box>
            <Box fontWeight={600}>{params.row.subscription.planName + " - " + params.row.subscription?.planOptionSelection}</Box>
            <Box fontSize={10}>
              {(params.row.subscription.planOptionMealTypes?.split(",")?.map((i: string) => mealTypeList.find((li) => li.name === i)?.description) ?? []).join(", ")}
            </Box>
          </Box>
          )
        )
      },
      {
        field: "subscription.startDate",
        minWidth: 80,
        flex: 1,
        headerName: "Start Date",
        valueGetter: (params: any) =>
          params.row.subscription ? moment(params.row.subscription.startDate).format(displayFormat) : "-",
      },
      {
        field: "subscription.endDate",
        minWidth: 80,
        flex: 1,
        headerName: "End Date",
        valueGetter: (params: any) =>
          params.row.subscription
            ? params.row.subscription.endDate
              ? moment(params.row.subscription.endDate).format(displayFormat)
              : "Paused"
            : "-",
      },
      {
        field: "subscription.active",
        minWidth: 80,
        flex: 1,
        headerName: "Subscription",
        renderCell: getUserSubscriptionStatus,
      },
    ];
  }, [users]);

  const filterItems: DataTableFilterItem[] = [
    {
      field: "planId",
      label: "Plan",
      type: "select",
      listData: plans,
    },
    {
      field: "dietitianId",
      label: "Dietitian",
      type: "select",
      listData: dietitians,
    },
    {
      rangeField: { min: "minBalance", max: "maxBalance" },
      label: "Balance",
      type: "numericRange",
    },
    {
      rangeField: { min: "minSubscriptionDate", max: "maxSubscriptionDate" },
      label: "Subscription Date",
      type: "dateRange",
    },
    {
      field: "channel",
      label: "Channel",
      type: "select",
      listData: channels,
    },
  ];

  const getUsers = async () => {
    setLoading(true);
    try {
      const res = await Api.User.listUsers(
        filterModel.query,
        filterModel.minBalance,
        filterModel.maxBalance,
        filterModel.minSubscriptionDate,
        filterModel.maxSubscriptionDate,
        filterModel.channel,
        filterModel.dietitianId,
        filterModel.planId,
        paginationModel.page,
        paginationModel.pageSize,
        sortModel.map((i) => `${i.field},${i.sort}`)
      );
      setUsers(res.data.content as PagedResponsePartnerUserListResponse[]);
      setTotalRowCount(Number(res.data.totalElements));
    } catch (error) {}
    setLoading(false);
  };

  useEffect(() => {
    getUsers();
  }, [paginationModel, sortModel, retriggerReq, filterModel]);

  //   useEffect(() => {
  //     setTotalRowCount((prevRowCountState) =>
  //       pageInfo?.totalRowCount !== undefined
  //         ? pageInfo?.totalRowCount
  //         : prevRowCountState,
  //     );
  //   }, [pageInfo?.totalRowCount, setRowCountState]);

  return (
    <Box className="users">
      <Subheader page="users" />
      <Box p={6} className="users-container">
        <DataTable
          rows={users}
          rowCount={totalRowCount}
          columns={columns}
          sortingMode="server"
          sortModel={sortModel}
          paginationMode="server"
          paginationModel={paginationModel}
          paginationChange={handlePaginationChange}
          sortChange={handleSortChange}
          filterItems={filterItems}
          filterModel={filterModel}
          filterModelChange={setFilterModel}
          rowClick={handleRowClick}
          getRowNavigationUrl={getRowNavigationUrl}
          loading={loading}
          create={create}
          deleteRec={deleteRec}
          options={{ hideActions: true, entityName: "user", permission: { create: "USER_CREATE" }, filter: true }}
        />
      </Box>
      <UserCrud create open={createShow} title="Create New User" onClose={handleClose} onConfirm={handleConfirm} />
      <ConfirmDialog />
    </Box>
  );
}
